import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Gauge, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
// v312a-fix-19 obj-safe: accept objects and non-strings
const _toStr = (u) => {
  if (!u) return '';
  if (typeof u === 'string') return u;
  if (typeof u === 'object') return u.url || u.src || u.href || '';
  return '';
};
const imgUrl = (u) => {
  const s = _toStr(u);
  if (!s) return '';
  if (s.startsWith('/')) return `${API}${s}`;
  try { return encodeURI(s); } catch { return s; }
};

// Zerlegt eine HTML-Beschreibung in saubere Absätze (Fallback, wenn keine
// KI-/gescrapten Premium-Absätze vorhanden sind).
const htmlToParas = (raw) => {
  if (!raw) return [];
  const text = raw
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+/g, ' ');
  return text.split(/\n+/).map((p) => p.trim()).filter((p) => p.length > 40);
};

/**
 * Maßgeschneiderte Ultra-Premium-Produktstory für NOVITEC (und andere
 * Premium-Marken mit design_style='novitec-premium'). Cinematisches
 * Fahrzeug-Hero, immersive Marketing-Bänder und eine Premium-Galerie mit
 * Lightbox – bewusst edel/dunkel im Ferrari-Boutique-Look.
 */
export default function NovitecPremium({ product, images = [] }) {
  const [lightbox, setLightbox] = useState(null);
  const rootRef = useRef(null);

  const brand = product.manufacturer || 'NOVITEC';
  const vehicle = [product.vehicle_brand, product.vehicle_model].filter(Boolean).join(' ').trim();
  const heroImg = product.vehicle_hero_image || images[0] || product.image_url;

  const paragraphs = useMemo(() => {
    let p = (product.premium_paragraphs || []).filter((x) => x && x.trim().length > 20);
    if (!p.length && Array.isArray(product.story_sections)) {
      p = product.story_sections.flatMap((s) => s.paragraphs || []).filter((x) => x && x.length > 20);
    }
    if (!p.length) p = htmlToParas(product.long_description || product.description);
    return p.slice(0, 6);
  }, [product]);

  const galleryImages = useMemo(
    () => [...new Set(images.filter(Boolean))],
    [images]
  );

  const intro = product.model_intro || paragraphs[0] || '';
  const bandParas = product.model_intro ? paragraphs : paragraphs.slice(1);

  const closeLb = useCallback(() => setLightbox(null), []);
  const nav = useCallback((dir) => {
    setLightbox((i) => {
      if (i == null) return i;
      const n = i + dir;
      if (n < 0) return galleryImages.length - 1;
      if (n >= galleryImages.length) return 0;
      return n;
    });
  }, [galleryImages.length]);

  useEffect(() => {
    if (lightbox == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLb();
      if (e.key === 'ArrowRight') nav(1);
      if (e.key === 'ArrowLeft') nav(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, closeLb, nav]);

  return (
    <div className="nv-premium" data-testid="novitec-premium" ref={rootRef}>
      {/* Cinematic Vehicle Hero */}
      {heroImg && (
        <section className="nv-hero" data-testid="novitec-hero-section"
                 style={{ backgroundImage: `url(${imgUrl(heroImg)})` }}>
          <div className="nv-hero__grain" />
          <div className="nv-hero__scrim" />
          <div className="nv-hero__inner">
            <motion.span className="nv-eyebrow"
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}>
              {brand}{vehicle ? ` · ${vehicle}` : ''}
            </motion.span>
            <motion.h2 className="nv-hero__title" data-testid="novitec-hero-title"
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}>
              {product.name}
            </motion.h2>
            {intro && (
              <motion.p className="nv-hero__lead"
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}>
                {intro}
              </motion.p>
            )}
            {(product.performance_note || vehicle) && (
              <motion.div className="nv-hero__stats"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.35 }}>
                {product.performance_note && (
                  <span className="nv-stat" data-testid="novitec-performance-note">
                    <Gauge size={16} strokeWidth={1.5} /> {product.performance_note}
                  </span>
                )}
                <span className="nv-stat">
                  <ShieldCheck size={16} strokeWidth={1.5} /> Original NOVITEC Veredelung
                </span>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Immersive Marketing-Bänder */}
      {bandParas.length > 0 && (
        <section className="nv-bands" data-testid="novitec-marketing-bands">
          {bandParas.map((p, i) => (
            <motion.div key={i} className={`nv-band ${i % 2 ? 'nv-band--right' : ''}`}
              initial={{ opacity: 0, y: 48 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }} transition={{ duration: 0.8 }}>
              {i === 0 && <span className="nv-accent-bar" />}
              <p className="nv-band__text">{p}</p>
            </motion.div>
          ))}
        </section>
      )}

      {/* Premium-Galerie */}
      {galleryImages.length > 0 && (
        <section className="nv-gallery-sec" data-testid="novitec-gallery">
          <div className="nv-sec-head">
            <span className="nv-eyebrow">{brand} · Details</span>
            <h3 className="nv-sec-title">GALE<strong>RIE</strong></h3>
          </div>
          <div className="nv-gallery">
            {galleryImages.map((img, i) => (
              <button key={i} className="nv-gallery__item" onClick={() => setLightbox(i)}
                      data-testid={`novitec-gallery-thumb-${i}`} aria-label={`Bild ${i + 1} öffnen`}>
                <img src={imgUrl(img)} alt={`${product.name} Bild ${i + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        </section>
      )}

      {lightbox != null && galleryImages[lightbox] && (
        <div className="nv-lightbox" onClick={closeLb} data-testid="novitec-lightbox">
          <button className="nv-lightbox__close" onClick={closeLb} aria-label="Schließen"><X size={28} /></button>
          {galleryImages.length > 1 && (
            <button className="nv-lightbox__nav nv-lightbox__nav--prev"
                    onClick={(e) => { e.stopPropagation(); nav(-1); }} aria-label="Zurück"><ChevronLeft size={32} /></button>
          )}
          <img className="nv-lightbox__img" src={imgUrl(galleryImages[lightbox])} alt={product.name}
               onClick={(e) => e.stopPropagation()} />
          {galleryImages.length > 1 && (
            <button className="nv-lightbox__nav nv-lightbox__nav--next"
                    onClick={(e) => { e.stopPropagation(); nav(1); }} aria-label="Weiter"><ChevronRight size={32} /></button>
          )}
          <span className="nv-lightbox__count">{lightbox + 1} / {galleryImages.length}</span>
        </div>
      )}
    </div>
  );
}
